import type {
  ControllerOptions,
  WebController,
  RequestCapture,
  HttpObservation,
  EarlyScreenEntry,
} from "@wishlink/applens-web";
import type { ScreenCompletion } from "@wishlink/applens-core";
import type {
  Clock,
  HomeHandle,
  HomeState,
  LocalConfig,
  DeploymentConfig,
} from "./contracts";
import { safely } from "./contracts";
import { RELEASE_2_ENDPOINTS } from "./registry";
import { createDiyJourney } from "./diyJourney";
import { createIntentDependency } from "./intentDependency";
import type { IntentSnapshot } from "./intentDependency";
import type { BrowserSignals } from "./browserSignals";
import type { ScreenHandle } from "@wishlink/applens-web";
import {
  HOME_TERMINAL_SCHEMA,
  dependencySnapshot,
  queryTiming,
} from "./dependencies";
import type { DependencyTimings, HomeDependencies } from "./dependencies";

import { createEarlyRequestBuffer } from "./earlyRequestBuffer";
import { createEarlyScreenRecorder } from "./earlyScreenRecorder";
import {
  createEarlyRequestCapture,
  mayBufferEarlyRequest,
} from "./earlyRequestCapture";

export const HOME_ERRORS = [
  { code: "home.blocking", category: "application" as const },
  { code: "home.partial", category: "application" as const },
];
export function homeCompletion(state: HomeState): ScreenCompletion | undefined {
  if (state.redirecting) return { outcome: "cancelled" };
  if (state.blockingError)
    return { outcome: "full_error", errorCode: "home.blocking" };
  if (
    state.configReady &&
    state.creatorReady &&
    state.branchCommitted &&
    !state.socialPending
  )
    return state.partialError
      ? { outcome: "degraded", errorCode: "home.partial" }
      : { outcome: "ready" };
  return undefined;
}
export function isLoopback(origin: string) {
  return safely(() => {
    const url = new URL(origin);
    return (
      ["http:", "https:"].includes(url.protocol) &&
      ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname) &&
      url.origin === origin
    );
  }, false);
}
interface Dependencies {
  signals?: BrowserSignals;
  mode?: ControllerOptions["mode"];
  deploymentEndpoint?: ControllerOptions["destination"];
  clock: Clock;
  timeOrigin?(): number;
  visible?(): boolean;
  origin: string;
  bootstrapDeadline?: number;
  allowDeploymentPropagation?: boolean;
  loadBinding?: ControllerOptions["loadBinding"];
  schedule: ControllerOptions["schedule"];
  defer(work: () => void): void;
  lifecycle?: ControllerOptions["lifecycle"];
}
/** Creator composition: readiness, Strict Mode reference counting and RTK snapshots. */
export function createController(deps: Dependencies) {
  const mode = deps.mode ?? (isLoopback(deps.origin) ? "local" : "deployment");
  const destination = deps.deploymentEndpoint && { ...deps.deploymentEndpoint };
  const deploymentPropagation = deps.allowDeploymentPropagation === true;
  let sdk: WebController | undefined;
  let coreRules: typeof import("@wishlink/applens-core") | undefined;
  let attachHeaders:
    | typeof import("@wishlink/applens-web/requests").attachRequestHeaders
    | undefined;
  let earlyPolicy:
    | {
        cachedDecision: string | null;
        scope: string;
        bucket?: number;
        visible(): boolean;
      }
    | undefined;
  const mayBuffer = () =>
    safely(
      () =>
        (mode === "local" && (deps.visible?.() ?? true)) ||
        (!!earlyPolicy &&
          earlyPolicy.visible() &&
          mayBufferEarlyRequest({
            localMode: false,
            ...earlyPolicy,
            now: deps.clock.epochNow,
          })),
      false
    );
  const requests = createEarlyRequestCapture<RequestCapture, HttpObservation>({
    mayBuffer,
    createBuffer: () =>
      createEarlyRequestBuffer({
        mayCapture: mayBuffer,
        clock: deps.clock,
        schedule: deps.schedule,
      }),
  });
  let early: ReturnType<typeof createEarlyScreenRecorder> | undefined;
  let earlySealed = false;
  let replayEntries: readonly EarlyScreenEntry[] | undefined;
  function clearEarly() {
    earlySealed = true;
    if (navigation?.early) navigation.closed = true;
    early?.clear();
  }
  const earlyScreens = {
    clear: clearEarly,
    drain() {
      if (earlySealed || !mayBuffer()) {
        clearEarly();
        return { screens: [] };
      }
      earlySealed = true;
      const batch = early?.drain() ?? {
        screens: [],
        requests: requests.drainCompleted().map(sdkRequest),
      };
      replayEntries = batch.screens;
      return batch;
    },
  };
  function sdkRequest(entry: import("./earlyRequestBuffer").EarlyRequestEntry) {
    return {
      ...entry,
      result:
        entry.result === "failure"
          ? { failure: true as const }
          : { status: entry.result },
    };
  }
  function captureEarly(key: string) {
    if (earlySealed || !mayBuffer()) return;
    early ??= createEarlyScreenRecorder({
      mayCapture: mayBuffer,
      clock: deps.clock,
      timeOrigin: deps.timeOrigin,
      requests: () => requests.drainCompleted().map(sdkRequest),
    });
    return early.start(key);
  }
  let generation = -1;
  let identity: string | undefined;
  let normalizeIdentity:
    | typeof import("@wishlink/applens-core").normalizeIdentity
    | undefined;
  let config: Readonly<LocalConfig> | undefined;
  let revision = 0;
  let removeSignals: (() => void) | undefined;
  let firstHome = true;
  // Document-scoped history survives activation changes and visibility transitions.
  let homeIsFirstRenderedRoute = true;
  function renderedRoute(isHome: boolean) {
    if (!isHome) homeIsFirstRenderedRoute = false;
  }
  const loaders = new Set<object>();
  const diy = createDiyJourney({
    now: deps.clock.now,
    schedule: deps.schedule,
    start: (entry) => {
      if (!active()) return;
      const journey = sdk?.startJourney("diy_single_product", { entry });
      loaders.forEach(() => journey?.loader("start"));
      return journey;
    },
  });
  let suspended = false;
  let hiddenAt: number | undefined;
  let readDependencies: () => DependencyTimings & {
    intent?: IntentSnapshot;
  } = () => ({});
  type Navigation = {
    handle: HomeHandle;
    references: number;
    state: HomeState;
    pending?: boolean;
    closed: boolean;
    revision: number;
    early?: ReturnType<NonNullable<typeof early>["start"]>;
    continued?: boolean;
    route: string;
    attempt?: ScreenHandle;
    intent: ReturnType<typeof createIntentDependency>;
    launch?: number;
    content?: boolean;
    launchContent?: number;
    document?: ReturnType<NonNullable<BrowserSignals["document"]>["startHome"]>;
    dependencies: (latest: DependencyTimings) => HomeDependencies;
  };
  let navigation: Navigation | undefined;
  function continueEarly(record: Navigation) {
    if (!record.closed && !record.continued && record.early?.transferred) {
      // Reuse an accepted attempt or validate the original start if replay dropped it.
      // Never substitute configure time for an early mount.
      replayEntries = undefined;
      record.attempt = sdk?.startScreen(
        record.route,
        record.handle.key,
        record.early.timestamp
      );
      record.revision = revision;
      record.continued = true;
    }
  }
  // The SDK cancels the previous screen synchronously inside startScreen. Keep its
  // reader installed until that call returns, then install the new record's closure.
  let terminalReader:
    | ((
        outcome: ScreenCompletion["outcome"]
      ) => Readonly<Record<string, string | number | boolean>> | undefined)
    | undefined;
  function readTerminal(
    start: { startedAt: number },
    result: { outcome: ScreenCompletion["outcome"]; durationMs: number }
  ) {
    if (!replayEntries) return terminalReader?.(result.outcome);
    const entry = replayEntries.find(
      (item) => item.startMono === start.startedAt
    );
    if (!entry || !coreRules) return;
    replayEntries = replayEntries.filter((item) => item !== entry);
    if (navigation && navigation.handle.key === entry.key)
      navigation.closed = true;
    const snapshots = entry.marks.filter(
      (mark) =>
        mark.kind === "dependencies" &&
        mark.at <= start.startedAt + result.durationMs
    );
    const initial =
      snapshots[0]?.kind === "dependencies" ? snapshots[0].value : {};
    const latestMark = snapshots[snapshots.length - 1];
    const latest =
      latestMark?.kind === "dependencies" ? latestMark.value : initial;
    return {
      ...coreRules.createDependencies(
        entry.timeOrigin + entry.startMono,
        initial
      )(latest),
      ...createIntentDependency(
        entry.timeOrigin + entry.startMono,
        { enabled: !!initial.intent, query: initial.intent },
        coreRules.dependencyState
      )({ enabled: !!latest.intent, query: latest.intent }),
    };
  }
  const active = () => {
    if (config && deps.clock.epochNow() >= config.expiresAt) disable();
    return !suspended && config ? config : undefined;
  };
  function disable() {
    clearEarly();
    requests.discard();
    if (navigation) navigation.closed = true;
    resetForConfiguration();
  }
  // Normal ON configuration must preserve this document's pre-SDK buffer.
  function resetForConfiguration() {
    revision++;
    config = undefined;
    removeSignals?.();
    removeSignals = undefined;
    diy.disable();
    sdk?.disable();
  }
  async function configure(
    input: LocalConfig | DeploymentConfig,
    requestedMode: "local" | "deployment"
  ) {
    const nextGeneration = safely(() => input.generation, NaN);
    if (!Number.isSafeInteger(nextGeneration) || nextGeneration <= generation) {
      disable();
      return false;
    }
    generation = nextGeneration;
    if (
      input.enabled !== true ||
      input.eligible !== true ||
      input.exportEnabled !== true
    )
      disable();
    else resetForConfiguration();
    suspended = false;
    const expected = revision;
    // OFF/excluded activation does not fetch even the portable SDK control plane.
    if (!sdk && (input.enabled !== true || input.eligible !== true))
      return false;
    if (!sdk) {
      let loaded: [
        typeof import("@wishlink/applens-web"),
        typeof import("@wishlink/applens-web/requests"),
        typeof import("@wishlink/applens-core")
      ];
      try {
        loaded = await Promise.all([
          import(
            /* webpackChunkName: "applens-control" */ "@wishlink/applens-web"
          ),
          import(
            /* webpackChunkName: "applens-requests" */ "@wishlink/applens-web/requests"
          ),
          import(
            /* webpackChunkName: "applens-core" */ "@wishlink/applens-core"
          ),
        ]);
      } catch {
        if (expected === revision) disable();
        return false;
      }
      const [module, requests, core] = loaded;
      coreRules = core;
      if (expected !== revision) return false;
      attachHeaders = requests.attachRequestHeaders;
      normalizeIdentity = core.normalizeIdentity;
      const normalized = normalizeIdentity(input.identity);
      if (
        !normalized ||
        normalized.runtime !== "web" ||
        !(mode === "local"
          ? normalized.environment === "local"
          : ["staging", "production"].includes(normalized.environment))
      ) {
        disable();
        return false;
      }
      identity = JSON.stringify(normalized);
      sdk = module.createWebController({
        mode,
        ...(mode === "deployment"
          ? { destination, allowDeploymentPropagation: deploymentPropagation }
          : {}),
        identity: normalized,
        origin: deps.origin,
        clock: deps.clock,
        schedule: deps.schedule,
        loadBinding: deps.loadBinding,
        earlyScreens,
        instrumentationName: "wishlink-creator",
        endpoints: RELEASE_2_ENDPOINTS.map(({ name, owner, method, path }) => ({
          name,
          owner,
          method,
          path,
        })),
        journeys: [
          {
            name: "diy_single_product",
            taxonomy: {
              product: "creator",
              feature: "diy",
              flow: "single_product",
            },
          },
        ],
        errors: HOME_ERRORS,
        bootstrap: {
          timeoutMs: Math.max(
            1,
            Math.min(
              60000,
              (deps.bootstrapDeadline ?? Infinity) - deps.clock.now()
            )
          ),
          maxStarts: 64,
          taxonomy: { product: "creator", feature: "navigation", flow: "home" },
        },
        healthScreens: ["home"],
        screens: [
          {
            routeId: "login",
            taxonomy: {
              product: "creator",
              feature: "navigation",
              flow: "login",
            },
            readTerminalAttributes: readTerminal,
          },
          {
            routeId: "home",
            taxonomy: {
              product: "creator",
              feature: "navigation",
              flow: "home",
            },
            terminalAttributes: HOME_TERMINAL_SCHEMA,
            readTerminalAttributes: readTerminal,
          },
        ],
        lifecycle: deps.lifecycle && {
          listen(hidden, visible) {
            return deps.lifecycle!.listen(
              () => {
                clearEarly();
                suspended = true;
                hiddenAt = deps.clock.now();
                if (navigation) navigation.closed = true;
                hidden();
              },
              () => {
                suspended = false;
                visible();
                if (
                  hiddenAt !== undefined &&
                  deps.clock.now() - hiddenAt > 300000
                )
                  safely(
                    () => diy.capture()?.finish("backgrounded"),
                    undefined
                  );
                hiddenAt = undefined;
              }
            );
          },
        },
      });
    }
    const permitted =
      requestedMode === mode &&
      identity === JSON.stringify(normalizeIdentity!(input.identity)) &&
      (mode !== "local" ||
        (input as LocalConfig).exporterPath === "/client/v1/traces");
    sdk.setContext(safely(() => deps.signals?.context(), {}) ?? {});
    const enabled = await sdk.configure(
      permitted ? input : { ...input, enabled: false }
    );
    replayEntries = undefined;
    if (expected !== revision) return false;
    if (!enabled) {
      disable();
      return false;
    }
    // No await/deferred callback may separate configure from the completed-entry flush.
    requests.configured({
      captureRequest: sdk.captureRequest,
      startRequest: sdk.startRequest,
      recordEarlyRequest: (entry) => sdk!.recordEarlyRequest(sdkRequest(entry)),
    });
    config = Object.freeze({
      ...input,
      apiOrigins: Object.freeze([...input.apiOrigins]),
    }) as Readonly<LocalConfig>;
    if (
      navigation?.early?.transferred &&
      !navigation.closed &&
      navigation.references > 0
    ) {
      continueEarly(navigation);
      navigation.early = undefined;
    }
    removeSignals = deps.signals?.listen(() =>
      safely(() => sdk?.setContext(deps.signals?.context() ?? {}), undefined)
    );
    return true;
  }
  // Optional until beta.2 is published and consumed in its own exact-version PR.
  function renewLease(input: DeploymentConfig): boolean {
    if (
      !active() ||
      !sdk ||
      !config ||
      input.generation <= generation ||
      identity !== JSON.stringify(normalizeIdentity!(input.identity))
    )
      return false;
    const renewable = sdk as WebController & {
      renewLease?: (activation: DeploymentConfig) => boolean;
    };
    if (!renewable.renewLease?.(input)) return false;
    generation = input.generation;
    config = Object.freeze({
      ...config,
      generation,
      expiresAt: input.expiresAt,
    });
    return true;
  }
  function enter(
    key: string,
    routeInput: boolean | "home" | "login" | "other",
    rendered = true
  ): HomeHandle {
    const route =
      typeof routeInput === "boolean"
        ? routeInput
          ? "home"
          : "other"
        : routeInput;
    const isHome = route === "home";
    if (rendered) renderedRoute(isHome);
    const launchEligible = firstHome && homeIsFirstRenderedRoute;
    let epoch = deps.clock.epochNow();
    if (navigation?.handle.key === key) {
      navigation.references++;
      return navigation.handle;
    }
    safely(() => navigation?.handle.finish({ outcome: "cancelled" }), false);
    const expected = revision;
    const live = active();
    const captured = !live && isHome ? captureEarly(key) : undefined;
    if (captured)
      epoch = captured.timestamp.timeOrigin + captured.timestamp.startMono;
    const initial = dependencySnapshot(
      live || captured ? safely(readDependencies, {}) : {}
    );
    let intentReader: ReturnType<typeof createIntentDependency> | undefined;
    let dependencyReader:
      | ((latest: DependencyTimings) => HomeDependencies)
      | undefined;
    const record: Navigation = {
      handle: undefined as unknown as HomeHandle,
      references: 1,
      state: {},
      closed: route === "other" || (!live && !captured),
      revision: expected,
      early: captured,
      route,
      document:
        isHome && launchEligible
          ? deps.signals?.document?.startHome()
          : undefined,
      intent: (latest) => {
        if (coreRules)
          intentReader ??= createIntentDependency(
            epoch,
            initial.intent,
            coreRules.dependencyState
          );
        return intentReader?.(latest) ?? { "app.dep.intent": "absent" };
      },
      dependencies: (latest) => {
        if (coreRules)
          dependencyReader ??= coreRules.createDependencies(epoch, initial);
        return (
          dependencyReader?.(latest) ?? {
            "app.dep.creator_config": "absent",
            "app.dep.creator_data": "absent",
          }
        );
      },
    };
    sdk?.setContext(deps.signals?.context() ?? {});
    const attempt =
      captured?.handle ?? (live ? sdk?.startScreen(route, key) : undefined);
    record.attempt = attempt;
    if (captured)
      attempt?.setDependencies({
        ...initial,
        intent: initial.intent.enabled ? initial.intent.query : undefined,
      });
    navigation = record;
    const readRecordTerminal = (outcome: ScreenCompletion["outcome"]) => {
      record.closed = true;
      const timing = record.document?.finish(outcome);
      if (timing)
        record.attempt?.setDocumentTimings({
          documentToHomeStartMs: timing["app.document_to_home_start_ms"],
          documentToContentMs: timing["app.document_to_content_ms"],
          documentToReadyMs: timing["app.document_to_ready_ms"],
          documentResponseEndMs: timing["app.document.response_end_ms"],
          documentDomReadyMs: timing["app.document.dom_ready_ms"],
          launchToDocumentMs: timing["app.launch_to_document_ms"],
        });
      if (!isHome) return undefined;
      const snapshot = dependencySnapshot(safely(readDependencies, {}));
      record.attempt?.setDependencies({
        ...snapshot,
        intent: snapshot.intent.enabled ? snapshot.intent.query : undefined,
      });
      return {
        ...record.dependencies(snapshot),
        ...record.intent(snapshot.intent ?? { enabled: false }),
      };
    };
    terminalReader = readRecordTerminal;
    if (isHome) {
      const startType = deps.signals?.start(launchEligible, deps.clock.now());
      if (!record.closed && startType) attempt?.setStartType(startType);
      if (!record.closed) {
        const anchor = deps.signals?.claimLaunch(
          launchEligible,
          startType === "cold"
        );
        if (anchor !== undefined)
          record.launch = deps.clock.now() - (epoch - anchor);
      }
      firstHome = false;
    }
    if (!record.closed) loaders.forEach(() => attempt?.loader("start"));
    const root = !record.closed
      ? {
          get closed() {
            if (record.closed) return true;
            if (record.early)
              return (
                !record.continued &&
                !record.early.transferred &&
                !record.early.recording
              );
            return revision !== record.revision || !active();
          },
        }
      : undefined;
    const handle: HomeHandle = {
      key,
      root,
      finish: (completion) =>
        safely(() => {
          continueEarly(record);
          if (!root || root.closed) return false;
          // readRecordTerminal snapshots typed dependencies exactly once, for
          // both explicit finishes and SDK-driven timeout/cancellation.
          if (
            record.launch !== undefined &&
            ["ready", "degraded"].includes(completion.outcome)
          )
            record.attempt?.setLaunch({
              launchToContentMs: record.launchContent,
              launchToReadyMs: deps.clock.now() - record.launch,
            });
          if (record.early && !record.continued) {
            readRecordTerminal(completion.outcome);
            return record.attempt?.finish(completion) ?? false;
          }
          return record.attempt?.finish(completion) ?? false;
        }, false),
      report(state) {
        if (navigation !== record) return;
        continueEarly(record);
        if (!root || root.closed) return;
        record.state = { ...record.state, ...state };
        if (record.pending) return;
        record.pending = true;
        deps.defer(() => {
          record.pending = false;
          if (navigation !== record) return;
          continueEarly(record);
          if (root.closed) return;
          if (!record.content && record.state.branchCommitted) {
            record.document?.content();
            record.content = true;
            if (record.launch !== undefined) {
              record.launchContent = deps.clock.now() - record.launch;
              record.attempt?.setLaunch({
                launchToContentMs: record.launchContent,
              });
            }
          }
          const completion =
            record.route === "login"
              ? record.state.interactive && !loaders.size
                ? { outcome: "ready" as const }
                : undefined
              : homeCompletion(record.state);
          if (
            loaders.size &&
            ["ready", "degraded"].includes(completion?.outcome ?? "")
          )
            return;
          if (completion) handle.finish(completion);
        });
      },
    };
    record.handle = handle;
    return handle;
  }
  return {
    configure: (input: LocalConfig) => configure(input, "local"),
    configureDeployment: (input: DeploymentConfig) =>
      configure(input, "deployment"),
    renewLease,
    resetForConfiguration,
    setEarlyCapturePolicy(policy: NonNullable<typeof earlyPolicy>) {
      earlyPolicy = policy;
    },
    setDependencyReader(
      reader: () => DependencyTimings & { intent?: IntentSnapshot }
    ) {
      readDependencies = reader;
    },
    attachRequestHeaders: (
      request: Request,
      observation?: Parameters<NonNullable<typeof attachHeaders>>[1]
    ) => attachHeaders?.(request, observation) ?? request,
    queryTiming,
    captureRequest: (name: string) =>
      !navigation?.handle.root &&
      !diy.capture() &&
      deps.clock.now() >= (deps.bootstrapDeadline ?? Infinity)
        ? undefined
        : requests.captureRequest(name),
    startRequest: (
      token: ReturnType<typeof requests.captureRequest>,
      request: { origin: string; method: string; url?: string }
    ): HttpObservation | undefined => {
      if (!token) return;
      const observation = token.startRequest(request);
      if (!observation) return;
      // Buffered observations cannot inject propagation, even after SDK activation.
      return token.kind === "buffered"
        ? {
            finish: observation.finish,
            allowedOrigins: [],
            headers: () => ({}),
            discard: () => {},
          }
        : (observation as HttpObservation);
    },
    exportHealth: () =>
      sdk?.exportHealth() ?? {
        disabled: { batches: 0, spans: 0 },
        busy: { batches: 0, spans: 0 },
        serialization: { batches: 0, spans: 0 },
        failed: { batches: 0, spans: 0 },
        split: { batches: 0, spans: 0 },
        oversized: { batches: 0, spans: 0 },
      },
    disable,
    clearEarlyScreens: clearEarly,
    enter,
    renderedRoute,
    diy,
    acquireLoader() {
      const token = {};
      loaders.add(token);
      const screen = navigation;
      const journey = diy.capture();
      if (screen) continueEarly(screen);
      if (screen?.handle.root && !screen.handle.root.closed)
        screen.attempt?.loader("start");
      journey?.loader("start");
      let released = false;
      return () =>
        safely(() => {
          if (released) return;
          released = true;
          loaders.delete(token);
          // A loader already visible at route entry belongs to that new screen too.
          if (navigation) continueEarly(navigation);
          if (navigation?.handle.root && !navigation.handle.root.closed)
            navigation.attempt?.loader("end");
          diy.capture()?.loader("end");
          navigation?.handle.report({});
        }, undefined);
    },
    route(path: string) {
      diy.route(path);
    },
    leave(handle: HomeHandle) {
      if (navigation?.handle !== handle) return;
      const record = navigation;
      record.references--;
      deps.defer(() => {
        if (record.references <= 0) handle.finish({ outcome: "cancelled" });
      });
    },
    captureHome: () =>
      navigation?.route === "home" &&
      navigation?.handle.root &&
      !navigation.handle.root.closed
        ? navigation.handle
        : undefined,
    activeConfig: active,
  };
}
export type Controller = ReturnType<typeof createController>;
